import { queryOptions, useQuery } from '@tanstack/react-query';
import { AttributeChipData } from '../../../components/Prediction/types';
import { SelfServiceSessionService } from '../../generated';
import { ResourceStatus } from '../../resources/useOcInsightsEvents';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { transformSoilClassification } from '../soilClassification';

const selfServiceSoilClassificationKeys = {
  base: () => ['selfServiceSoilClassification'] as const,
  one: ({
    jobsiteId,
    sessionId,
    imageIndex,
    imageStatus,
    soilClassificationStatus,
  }: SelfServiceSoilClassificationOptionsProps) => [
    ...selfServiceSoilClassificationKeys.base(),
    jobsiteId,
    sessionId,
    imageIndex,
    imageStatus,
    soilClassificationStatus,
  ],
};

interface SelfServiceSoilClassificationOptionsProps {
  jobsiteId: string;
  sessionId: string;
  imageIndex: number | null;
  imageStatus: ResourceStatus;
  soilClassificationStatus: ResourceStatus;
  enabled?: boolean;
}

function selfServiceSoilClassificationOptions({
  jobsiteId,
  sessionId,
  imageIndex,
  imageStatus,
  soilClassificationStatus,
  enabled = true,
}: SelfServiceSoilClassificationOptionsProps) {
  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: selfServiceSoilClassificationKeys.one({
      jobsiteId,
      sessionId,
      imageIndex,
      imageStatus,
      soilClassificationStatus,
    }),
    queryFn: async () => {
      if (!jobsiteId || !sessionId || imageIndex === null)
        throw new Error('jobsiteId, sessionId and imageIndex are required');

      const response = await SelfServiceSessionService.selfServiceSessionControllerGetSessionImageSoilClassification({
        path: {
          jobsiteId,
          sessionId,
          index: imageIndex,
        },
      });

      if (response.data !== undefined) {
        return transformSoilClassification(response.data.output);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled:
      enabled &&
      !!jobsiteId &&
      !!sessionId &&
      imageIndex !== null &&
      imageStatus === 'finished' &&
      soilClassificationStatus === 'finished',
  });
}

export function useSelfServiceSoilClassification(props: SelfServiceSoilClassificationOptionsProps) {
  return useQuery(selfServiceSoilClassificationOptions(props));
}
